// Recent places (design spec 2026-10-19 §5 "Recent"; Work OS `RecentPlaces`): the last eight
// places the person opened in a company, in this browser (`localStorage companyos.recent.{id}`;
// client data, not a metric). A place is recorded from the screen's own h1 once it has
// rendered, so every mode's boards, channels, pages, seats and weeks count without each screen
// having to say so; Home itself is never a place.
import { useEffect } from "react";

export type RecentPlace = {
  href: string;
  label: string;
  /** The mode the place belongs to ("Work", "Chat", …). */
  mode: string;
  at: number;
};

const KEY = (companyId: string) => `companyos.recent.${companyId}`;
export const RECENT_MAX = 8;

const MODE_NAMES: Record<string, string> = {
  chat: "Chat",
  work: "Work",
  chart: "KPIs",
  kpis: "KPIs",
  cadence: "KPIs",
  agents: "Team",
  reports: "Reports",
  setup: "Setup",
};

export function readRecent(companyId: string): RecentPlace[] {
  try {
    const raw = window.localStorage.getItem(KEY(companyId));
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    if (!Array.isArray(list)) return [];
    return list.filter(
      (p): p is RecentPlace =>
        typeof p === "object" &&
        p !== null &&
        typeof (p as RecentPlace).href === "string" &&
        typeof (p as RecentPlace).label === "string",
    );
  } catch {
    return [];
  }
}

export function recordRecent(companyId: string, place: Omit<RecentPlace, "at">): RecentPlace[] {
  const next = [
    { ...place, at: Date.now() },
    ...readRecent(companyId).filter((p) => p.href !== place.href),
  ].slice(0, RECENT_MAX);
  try {
    window.localStorage.setItem(KEY(companyId), JSON.stringify(next));
  } catch {
    // Storage blocked: the list lives for this render only.
  }
  return next;
}

/** `/c/{slug}/{mode}/…` → the place's mode name, or null for Home and unknown paths. */
export function modeOfPath(pathname: string): string | null {
  const seg = pathname.split("/")[3] ?? "";
  return MODE_NAMES[seg] ?? null;
}

/**
 * Records the current screen as a recent place once its h1 has rendered (1.2 s after the path
 * changes, so lists and names have loaded). Mounted once per company layout.
 */
export function useRecordRecentPlace(companyId: string, pathname: string, search: string) {
  useEffect(() => {
    const mode = modeOfPath(pathname);
    if (!mode) return;
    const timer = window.setTimeout(() => {
      const h1 = document.querySelector("main h1");
      const label = h1?.textContent?.trim();
      if (!label) return;
      recordRecent(companyId, { href: `${pathname}${search}`, label, mode });
    }, 1200);
    return () => window.clearTimeout(timer);
  }, [companyId, pathname, search]);
}
